import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, RotateCcw, FileText, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import FiltroData from './FiltroData';
import FiltrosRapidosCompactos from './FiltrosRapidosCompactos';

const FiltrosOtimizados = ({
  range,
  setRange,
  loading,
  busca,
  setBusca,
  filtroTipoRecebimento,
  setFiltroTipoRecebimento,
  filtroArquivosAtual,
  setFiltroArquivosAtual,
  onRefresh,
  onClearAll,
  contasFiltradas = [],
  totalContas = 0,
  filtroAtivo,
  setFiltroAtivo,
  contasReceber = [],
  contasRecebidas = [],
}) => {
  const focusRingClass =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]';
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [mostrarTodosTipos, setMostrarTodosTipos] = useState(false);

  const [filtrosMinimizados, setFiltrosMinimizados] = useState(() => {
    try {
      const saved = localStorage.getItem('filtros_contas_receber_minimizados');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('filtros_contas_receber_minimizados', JSON.stringify(filtrosMinimizados));
    } catch (error) {
      console.warn('Erro ao salvar preferencia de filtros:', error);
    }
  }, [filtrosMinimizados]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filtrosAtivosCount = useMemo(
    () =>
      [
        busca ? 1 : 0,
        filtroTipoRecebimento !== 'todos' ? 1 : 0,
        filtroArquivosAtual !== 'todas' ? 1 : 0,
        range.presetId !== 'mes_atual' ? 1 : 0,
      ].reduce((a, b) => a + b, 0),
    [busca, filtroTipoRecebimento, filtroArquivosAtual, range.presetId]
  );

  const temFiltrosAtivos = filtrosAtivosCount > 0;

  const limparTodosFiltros = () => {
    setBusca('');
    setFiltroTipoRecebimento('todos');
    setFiltroArquivosAtual('todas');
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const pad = (n) => String(n).padStart(2, '0');
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setRange({ presetId: 'mes_atual', start: toISO(startOfMonth), end: toISO(endOfMonth) });
    if (onClearAll) onClearAll();
  };

  const getColorClasses = (isActive = false) => {
    const baseClasses =
      `px-3 py-1.5 rounded-[var(--cp-radius-pill,999px)] text-sm font-medium transition-all duration-[var(--cp-motion-fast,180ms)] ` +
      `flex items-center gap-2 border motion-reduce:transition-none ${focusRingClass}`;
    const minTouchTarget = 'min-h-[44px]';
    if (isActive) {
      return `${baseClasses} ${minTouchTarget} bg-[color:var(--accent)] text-white border-transparent shadow-sm`;
    }
    return `${baseClasses} ${minTouchTarget} bg-[color:var(--cp-surface-soft,var(--surface-muted))] text-[color:var(--text-primary)] border-[color:var(--cp-border,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))]`;
  };

  const tiposRecebimento = useMemo(
    () => [
      { id: 'todos', label: 'Todos' },
      { id: 'pix', label: 'PIX' },
      { id: 'boleto', label: 'Boleto' },
      { id: 'transferência', label: 'Transferência' },
      { id: 'dinheiro', label: 'Dinheiro' },
      { id: 'cartão', label: 'Cartão' },
      { id: 'cheque', label: 'Cheque' },
    ],
    []
  );

  const filtrosArquivos = [
    { id: 'todas', label: 'Todas' },
    { id: 'com_arquivos', label: 'Com arquivos' },
    { id: 'sem_arquivos', label: 'Sem arquivos' },
  ];

  const tiposVisiveis = useMemo(() => {
    if (!isMobile || mostrarTodosTipos) return tiposRecebimento;
    return tiposRecebimento.slice(0, 5);
  }, [isMobile, mostrarTodosTipos, tiposRecebimento]);

  return (
    <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--cp-border,var(--border))] mb-4 w-full max-w-full">
      <div className="p-4 border-b border-[color:var(--cp-border,var(--border))]">
        <FiltrosRapidosCompactos
          filtroAtivo={filtroAtivo}
          setFiltroAtivo={setFiltroAtivo}
          contasFiltradas={contasFiltradas}
          totalContas={totalContas}
          contasRecebidas={contasRecebidas}
          contasReceber={contasReceber}
        />
      </div>

      <div className="sticky top-0 z-40 border-b border-[color:var(--cp-border,var(--border))] bg-[color:var(--surface)]/95 p-4 backdrop-blur-sm md:static md:bg-transparent md:backdrop-blur-none">
        <div className="space-y-3">
          <div className="relative min-w-0">
            <input
              type="text"
              placeholder="Buscar por cliente, descrição, categoria ou ID..."
              className={`w-full min-h-[44px] rounded-lg border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-4 py-2.5 pr-10 text-sm text-[color:var(--text-primary)] ${focusRingClass}`}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar contas a receber"
            />
            <Search className="h-4 w-4 text-[color:var(--text-muted)] absolute right-3 top-1/2 -translate-y-1/2" />
          </div>

          <div className="flex items-center justify-end">
            <FiltroData value={range} loading={loading} onChange={setRange} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFiltrosMinimizados(!filtrosMinimizados)}
              className={`relative inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-200 hover:bg-[color:var(--cp-surface,var(--surface))] ${focusRingClass}`}
              title={filtrosMinimizados ? 'Mostrar filtros' : 'Ocultar filtros'}
              type="button"
              aria-expanded={!filtrosMinimizados}
              aria-controls="contas-receber-filtros-avancados"
            >
              {filtrosMinimizados ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              <span>Filtros ({filtrosAtivosCount})</span>
            </button>
            {temFiltrosAtivos && (
              <button
                onClick={limparTodosFiltros}
                className={`inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-200 hover:bg-[color:var(--cp-surface,var(--surface))] ${focusRingClass}`}
                title="Limpar todos os filtros"
                type="button"
                aria-label="Limpar filtros ativos"
              >
                <RotateCcw className="h-4 w-4" />
                Limpar
              </button>
            )}
            <button
              onClick={onRefresh}
              className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-200 hover:bg-[color:var(--cp-surface,var(--surface))] ${focusRingClass}`}
              title="Atualizar dados"
              type="button"
              aria-label="Atualizar dados da lista"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        id="contas-receber-filtros-avancados"
        className={`overflow-hidden transition-all duration-300 ease-in-out motion-reduce:transition-none ${filtrosMinimizados ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'}`}
      >
        <div className="p-4 w-full max-w-full">
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 w-full">
            <div className="flex-1 min-w-0 w-full lg:w-auto">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="h-4 w-4 text-[color:var(--text-muted)]" />
                <span className="text-sm font-medium text-[color:var(--text-primary)]">Tipo de Recebimento:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {tiposVisiveis.map((tipo) => (
                  <button
                    key={tipo.id}
                    onClick={() => setFiltroTipoRecebimento(tipo.id)}
                    className={getColorClasses(filtroTipoRecebimento === tipo.id)}
                    type="button"
                    aria-pressed={filtroTipoRecebimento === tipo.id}
                  >
                    {tipo.label}
                  </button>
                ))}
                {isMobile && tiposRecebimento.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setMostrarTodosTipos(!mostrarTodosTipos)}
                    className={`${getColorClasses(false)} px-3`}
                    aria-expanded={mostrarTodosTipos}
                  >
                    {mostrarTodosTipos ? 'Ver menos' : 'Ver mais'}
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 w-full lg:w-auto">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-[color:var(--text-muted)]" />
                <span className="text-sm font-medium text-[color:var(--text-primary)]">Arquivos:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {filtrosArquivos.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setFiltroArquivosAtual(item.id)}
                    className={getColorClasses(filtroArquivosAtual === item.id)}
                    type="button"
                    aria-pressed={filtroArquivosAtual === item.id}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltrosOtimizados;
